import React, { useEffect, useState } from 'react';
import { getMySubscription, setMySubscription } from '../services/weeklyReportSubscriptionAPI';

// WeeklyReportSubscriptionToggle affiche l'état d'abonnement courant à
// l'utilisateur authentifié et expose un bouton bascule. Le composant gère
// son propre état de chargement / erreur — pas de prop, pas de store global.
//
// Présentation alignée sur la charte MyJouleBox (jaune Joule, arrondis 20px,
// font Ubuntu via la typo globale du projet). Le bouton primaire agit en
// "désabonner" quand on est abonné, et en "réactiver" quand on est désabonné.
export default function WeeklyReportSubscriptionToggle() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getMySubscription()
      .then((res) => {
        if (!active) return;
        setSubscribed(res.subscribed);
        setEmail(res.email);
        setError(null);
      })
      .catch((err: { response?: { status?: number; data?: { error?: string } } }) => {
        if (!active) return;
        const status = err?.response?.status;
        const apiMsg = err?.response?.data?.error;
        // Surface des messages plus parlants pour faciliter le debug.
        if (status === 404) {
          setError("Endpoint absent côté API — l'instance api-data-platform doit être redémarrée avec la nouvelle version.");
        } else if (status === 401) {
          setError('Session expirée, reconnectez-vous.');
        } else if (apiMsg) {
          setError(`Erreur serveur : ${apiMsg}`);
        } else {
          setError("Impossible de récupérer votre statut d'abonnement.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const onToggle = async () => {
    if (subscribed == null || saving) return;
    setSaving(true);
    setError(null);
    try {
      const next = !subscribed;
      const res = await setMySubscription(next);
      setSubscribed(res.subscribed);
    } catch {
      setError('Action impossible. Réessayez plus tard.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        background: '#FFFFFF',
        borderRadius: 20,
        padding: 24,
        marginTop: 24,
        maxWidth: 640,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <h3 style={{ margin: '0 0 8px 0', fontSize: 18, display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Petit point jaune Joule en lieu et place de la bande horizontale —
            accent charte sans casser l'arrondi de la carte. */}
        <span style={{ width: 10, height: 10, borderRadius: 5, background: '#f8e04b' }} />
        Rapport hebdomadaire Minigrid
      </h3>
      <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: 14, lineHeight: 1.5 }}>
        Recevez tous les lundis matin un récapitulatif HTML par email avec les KPIs et graphiques
        des comptes Minigrid auxquels vous êtes rattaché.
      </p>

      {loading && <p style={{ margin: 0, color: '#6b7280' }}>Chargement…</p>}

      {!loading && subscribed != null && (
        <>
          <p style={{ margin: '0 0 12px 0', fontSize: 14 }}>
            <strong>{email || 'Votre adresse'}</strong> —{' '}
            {subscribed ? (
              <span style={{ color: '#6ca552', fontWeight: 700 }}>abonné</span>
            ) : (
              <span style={{ color: '#be3e3e', fontWeight: 700 }}>désabonné</span>
            )}
          </p>
          <button
            type="button"
            onClick={onToggle}
            disabled={saving}
            style={{
              padding: '10px 20px',
              borderRadius: 20,
              border: subscribed ? '2px solid #000' : 'none',
              background: subscribed ? '#FFFFFF' : '#f8e04b',
              color: '#000',
              fontWeight: 700,
              fontSize: 14,
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? '…' : subscribed ? 'Se désabonner' : "Réactiver l'envoi"}
          </button>
        </>
      )}

      {error && (
        <p style={{ marginTop: 12, color: '#be3e3e', fontSize: 13 }}>
          {error}
        </p>
      )}
    </div>
  );
}
